import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Divider, Flex, Input, Text } from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import i18next from "i18next";

import {
  clearRequestLog,
  DUP_WINDOW_MS,
  getRequestMonitorState,
  installRequestMonitor,
  setSlowWrite,
  subscribeRequestMonitor,
} from "./devRequestMonitor";
import { handleRemoteControl } from "../../../utils/remoteControl";

/**
 * 是否為「測試環境」:
 *  - 本機開發(npm start,NODE_ENV=development)
 *  - 部署測試站:build 時測試/正式都是 production,差異在 .env
 *    測試站 REACT_APP_WEB_NAME 帶「_T」、API/WS 走 snd./test 網域;正式站不含這些字樣。
 *  若日後測試站命名規則改變,調整這裡即可。
 */
export const isTestEnv =
  process.env.NODE_ENV === "development" ||
  /snd|test|_t/i.test(
    `${process.env.REACT_APP_API_URL || ""} ${
      process.env.REACT_APP_WS_HOST || ""
    } ${process.env.REACT_APP_WEB_NAME || ""}`,
  );

/* API 紀錄 / 連點偵測要在第一支請求之前就掛上,所以放模組層(正式站不會掛) */
installRequestMonitor(isTestEnv);

const GOLD = "#d3b552";
const BLUE = "#90caf9";
const TEST_REQ_ID = "TEST_REQ"; // transfer_in_game 測試用固定單號
const AUTO_SUCCESS_DELAY_MS = 5000; // 「pending → N 秒後完成」測試鈕的延遲

/**
 * DevTestPanel — 統一測試面板(僅測試環境顯示,正式站自動回傳 null)。
 *
 * 2026-10-12 與新站 SDK 的 WebSocketDevPanel 同步,區塊順序一致:
 *   ① 情境測試:連點偵測(一直開著)、寫入延遲 3 秒、API 紀錄 —— 見 devRequestMonitor.js
 *   ② 遊戲中上分:上分動畫、轉入 pending / success / 自動完成
 *   ③ 維護:game_id 命中 / 不含 / 會員層級、全站維護
 *   ④ 機台遠端關鎖(remote_off / remote_on 黑屏)、轉點 / Socket 錯誤
 *   ⑤ 後端訊息:逐則觸發 backend_response 的翻譯,Toast(API 錯誤)/ Socket(error 推播)兩條路徑
 *
 * WS 類按鈕透過 onProcess 呼叫「真實」的 processSocketMessage,測到的即實際邏輯、不會 drift。
 * 沒有傳 onProcess 的站(WS 處理還沒抽成函式)WS 類按鈕會停用,其餘區塊照常可用。
 *
 * @param {number|string} uid 會員 uid
 * @param {number|string|null} currentGameUid 當前遊戲 uid
 * @param {(parsed:{type:string, member_info_uid?:any, data?:any, memo?:any})=>void} [onProcess]
 * @param {(amount?:number)=>void} [onSimDeposit] 上分動畫(僅動畫)
 */
const DevTestPanel = ({ uid, currentGameUid, onProcess, onSimDeposit }) => {
  const dispatch = useDispatch();
  /* 機台編號:remote_off / remote_on 用它比對推播名單 */
  const machineNumber = useSelector(
    (state) => state.isMemberInfo?.machine_number,
  );
  const [open, setOpen] = useState(false);
  const [scenarioOpen, setScenarioOpen] = useState(true);
  const [logOpen, setLogOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [channel, setChannel] = useState("toast");
  const [keyword, setKeyword] = useState("");
  const [custom, setCustom] = useState("");
  const [, bump] = useState(0);
  // 「pending → 自動完成」倒數中,避免重複按而疊出多個計時器
  const [autoRunning, setAutoRunning] = useState(false);
  const autoTimerRef = useRef(null);

  // hooks 必須在 isTestEnv 早退之前宣告完
  useEffect(() => () => clearTimeout(autoTimerRef.current), []);
  useEffect(() => subscribeRequestMonitor(() => bump((n) => n + 1)), []);

  /* 後端訊息字典:直接讀已載入的語系(backend_response) */
  const backendMessages = useMemo(() => {
    if (!open || !msgOpen) return {};
    const dict = i18next.t("backend_response", { returnObjects: true });
    return dict && typeof dict === "object" ? dict : {};
  }, [open, msgOpen]);
  const filteredKeys = useMemo(() => {
    const keys = Object.keys(backendMessages);
    const k = keyword.trim().toLowerCase();
    if (!k) return keys;
    return keys.filter(
      (key) =>
        key.toLowerCase().includes(k) ||
        String(backendMessages[key]).toLowerCase().includes(k),
    );
  }, [backendMessages, keyword]);

  if (!isTestEnv) return null;

  const canWs = typeof onProcess === "function";
  const { slowWrite, log, dups } = getRequestMonitorState();

  const inGameBox =
    typeof window !== "undefined" &&
    window.location.pathname.includes("gamebox");

  const ws = (parsed) => {
    console.log("[test]", parsed.type, parsed);
    onProcess?.(parsed);
  };

  // 各類模擬
  const simMaintainMember = (mode) => {
    let data;
    if (mode === "matchGameId") {
      data = {
        msg: "【測試】當前遊戲維護(game_id 命中)",
        game_id: [currentGameUid],
      };
    } else if (mode === "otherGameId") {
      data = {
        msg: "【測試】其他遊戲維護(game_id 不含當前)",
        game_id: ["__NOT_MATCH__"],
      };
    } else {
      data = "【測試】會員層級維護(無 game_id)";
    }
    ws({ type: "maintain_notice_member", member_info_uid: uid, data });
  };

  const simTransferInGame = (status) => {
    const data =
      status === "pending"
        ? { request_id: TEST_REQ_ID, status: "pending" }
        : { request_id: TEST_REQ_ID, status, amount: 888, platform: "TEST" };
    ws({ type: "transfer_in_game", member_info_uid: uid, data });
  };

  /**
   * 轉點的完整流程:pending → (等後端) → success。
   * 這顆一次跑完:先送 pending,AUTO_SUCCESS_DELAY_MS 後自動送 success。
   */
  const simTransferInGameAuto = () => {
    if (autoRunning) return;
    setAutoRunning(true);
    simTransferInGame("pending");
    autoTimerRef.current = setTimeout(() => {
      simTransferInGame("success");
      setAutoRunning(false);
    }, AUTO_SUCCESS_DELAY_MS);
  };

  /**
   * 後端訊息兩條顯示路徑都要能單獨驗:
   *   toast  → 與 baseApi 攔截器同一條:API_ERROR → middleware → notice
   *   socket → WS error 推播 → processSocketMessage
   */
  const fireBackendMessage = (message) => {
    if (channel === "socket") {
      ws({ type: "error", member_info_uid: uid, data: message });
      return;
    }
    dispatch({ type: "API_ERROR", error: { response: { data: { message } } } });
  };

  const sectionTitle = (t, color = GOLD) => (
    <Text fontSize="10px" color={color} fontWeight="700" mt={1}>
      {t}
    </Text>
  );
  const btn = (
    label,
    onClick,
    scheme = "gray",
    { ws: needWs = false } = {},
  ) => (
    <Button
      size="xs"
      colorScheme={scheme}
      onClick={onClick}
      justifyContent="flex-start"
      isDisabled={needWs && !canWs}
      title={
        needWs && !canWs ? "這站的 WS 處理還沒抽成函式,不能模擬" : undefined
      }
    >
      {label}
    </Button>
  );
  const toggleBtn = (label, isOpen, onClick, color = GOLD) => (
    <Button
      size="xs"
      variant="outline"
      color={color}
      borderColor={color}
      justifyContent="flex-start"
      onClick={onClick}
      _hover={{ bg: "whiteAlpha.100" }}
    >
      {isOpen ? "▼" : "▶"} {label}
    </Button>
  );

  return (
    <Box position="fixed" left="8px" bottom="8px" zIndex={9999999}>
      {/* 收合開關 */}
      <Button
        size="xs"
        bg="rgba(0,0,0,0.8)"
        color={GOLD}
        border="1px solid rgba(211,181,82,0.6)"
        _hover={{ bg: "rgba(0,0,0,0.9)" }}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? "▼ 測試面板" : "▶ 測試面板"}
        {dups.length > 0 && (
          <Box as="span" ml={1} color="red.300">
            ⚠{dups.length}
          </Box>
        )}
      </Button>

      {open && (
        <Flex
          direction="column"
          gap="5px"
          mt="6px"
          p="8px"
          w="260px"
          maxH="70vh"
          overflowY="auto"
          bg="rgba(0,0,0,0.9)"
          borderRadius="8px"
          border="1px solid rgba(211,181,82,0.5)"
        >
          <Text fontSize="10px" color="whiteAlpha.700">
            uid:{String(uid ?? "無")} / 當前遊戲:
            {String(currentGameUid ?? "無")} / gamebox:
            {inGameBox ? "是" : "否"}
          </Text>
          {!canWs && (
            <Text fontSize="10px" color="orange.300">
              ⚠ 這站的 WS 處理還沒抽成函式,WS 類按鈕停用(情境測試、後端訊息
              Toast 可用)
            </Text>
          )}

          {/* ① 情境測試:連點偵測 / 寫入延遲 / API 紀錄 */}
          {toggleBtn(
            "情境測試",
            scenarioOpen,
            () => setScenarioOpen((v) => !v),
            BLUE,
          )}
          {scenarioOpen && (
            <Flex direction="column" gap="5px" pl={1}>
              <Box
                p="6px"
                borderRadius="4px"
                bg={
                  dups.length ? "rgba(198,40,40,0.35)" : "rgba(46,125,50,0.25)"
                }
                fontSize="11px"
                color="white"
              >
                <b>連點偵測</b>:重複送出{" "}
                <Box as="b" color={dups.length ? "red.200" : "green.200"}>
                  {dups.length}
                </Box>{" "}
                次
                <Text fontSize="10px" color="whiteAlpha.700">
                  同一支送出類 API {DUP_WINDOW_MS / 1000}{" "}
                  秒內送兩次就算(清空紀錄會歸零)
                </Text>
                {dups.slice(0, 5).map((d, i) => (
                  <Text
                    key={`${d.t}-${i}`}
                    fontSize="10px"
                    color="red.200"
                    wordBreak="break-all"
                  >
                    {d.t} {d.method} {d.url}
                  </Text>
                ))}
              </Box>

              <Box as="label" fontSize="11px" color="white" cursor="pointer">
                <input
                  type="checkbox"
                  checked={slowWrite}
                  onChange={(e) => setSlowWrite(e.target.checked)}
                  style={{ marginRight: 6, verticalAlign: "middle" }}
                />
                <b>寫入延遲 3 秒(測連點)</b>{" "}
                <Box as="span" color={BLUE} fontSize="9px">
                  ABANDON-SUBMIT-LOCK
                </Box>
                <Text fontSize="10px" color="whiteAlpha.600" ml="20px">
                  送出類請求晚 3 秒才送(會真的送出)。開著去快速連點棄分 /
                  確認鈕,上面的「重複送出」要維持 0
                </Text>
              </Box>

              <Flex gap="4px" wrap="wrap">
                {toggleBtn(
                  `API 紀錄(${log.length})`,
                  logOpen,
                  () => setLogOpen((v) => !v),
                  "gray.300",
                )}
                <Button size="xs" onClick={clearRequestLog}>
                  清空
                </Button>
                <Button
                  size="xs"
                  onClick={() =>
                    navigator.clipboard?.writeText(JSON.stringify(log, null, 2))
                  }
                >
                  複製
                </Button>
              </Flex>
              {logOpen &&
                log.map((e, i) => (
                  <Box
                    key={`${e.t}-${i}`}
                    fontSize="10px"
                    color="whiteAlpha.900"
                    borderBottom="1px solid #333"
                    py="3px"
                    wordBreak="break-all"
                  >
                    <Box as="span" color="whiteAlpha.500">
                      {e.t}
                    </Box>{" "}
                    <b>{e.method}</b> {e.url}{" "}
                    {e.dup && (
                      <Box as="span" color="red.300" fontWeight="700">
                        ⚠ 重複{" "}
                      </Box>
                    )}
                    {e.delayed && (
                      <Box as="span" color="orange.300">
                        延後 3 秒{" "}
                      </Box>
                    )}
                    <Box as="span" color="whiteAlpha.500">
                      {e.status ?? "…"}
                    </Box>
                    {e.body !== undefined && (
                      <Text color="green.200">{JSON.stringify(e.body)}</Text>
                    )}
                  </Box>
                ))}
            </Flex>
          )}

          {/* ② 遊戲中上分 */}
          {sectionTitle("遊戲中入鈔上分")}
          {btn("上分動畫 +321(僅動畫)", () => onSimDeposit?.(321), "green")}
          {btn(
            "轉入處理中 pending",
            () => simTransferInGame("pending"),
            "yellow",
            { ws: true },
          )}
          {btn(
            "轉入完成 success",
            () => simTransferInGame("success"),
            "green",
            { ws: true },
          )}
          {btn(
            autoRunning
              ? `完成倒數中…(${AUTO_SUCCESS_DELAY_MS / 1000}s)`
              : `pending → ${AUTO_SUCCESS_DELAY_MS / 1000} 秒後完成`,
            simTransferInGameAuto,
            "teal",
            { ws: true },
          )}

          {/* ③ 維護 */}
          {sectionTitle("維護")}
          {btn(
            "game_id 命中當前(需在 gamebox)",
            () => simMaintainMember("matchGameId"),
            "orange",
            { ws: true },
          )}
          {btn(
            "game_id 不含當前(應靜默)",
            () => simMaintainMember("otherGameId"),
            "gray",
            { ws: true },
          )}
          {btn(
            "無 game_id(會員層級,50s reload)",
            () => simMaintainMember("noGameId"),
            "purple",
            { ws: true },
          )}
          {btn(
            "全站維護 maintain_notice",
            () => ws({ type: "maintain_notice", data: "【測試】全站維護時間" }),
            "red",
            {
              ws: true,
            },
          )}

          {/* ④ 機台遠端關鎖(黑屏):名單帶目前機台編號,走跟真實推播同一段處理 */}
          {sectionTitle("機台遠端關鎖(黑屏)")}
          <Text
            fontSize="10px"
            color={machineNumber ? "whiteAlpha.700" : "orange.300"}
          >
            {machineNumber
              ? `機台編號:${machineNumber}(結果看 Console 的 [WS remote_off] / [WS remote_on];一般瀏覽器沒有外殼,不會真的黑屏)`
              : "⚠ 會員資料沒有機台編號(非機台帳號),名單對不上、不會有反應"}
          </Text>
          {["remote_off", "remote_on"].map((type) => (
            <Button
              key={type}
              size="xs"
              colorScheme={type === "remote_off" ? "blackAlpha" : "gray"}
              justifyContent="flex-start"
              onClick={() => {
                const parsed = {
                  type,
                  member_info_uid: [machineNumber ?? "__NO_MACHINE__"],
                  data: null,
                };
                /* 有 onProcess 走站台真正的 WS 處理;沒有(zun789)就直接呼叫同一支處理函式 */
                if (canWs) ws(parsed);
                else handleRemoteControl(parsed, machineNumber);
              }}
            >
              {type === "remote_off"
                ? "remote_off(打開黑屏 + 靜音)"
                : "remote_on(關閉黑屏 + 恢復音量)"}
            </Button>
          ))}

          {/* ④ 轉點 / 錯誤 */}
          {sectionTitle("轉點 / 錯誤")}
          {btn(
            "轉點失敗(顯示訊息)",
            () =>
              ws({
                type: "transCode",
                member_info_uid: uid,
                data: "fails",
                memo: "【測試】轉點失敗訊息",
              }),
            "gray",
            { ws: true },
          )}
          {btn(
            "轉點成功(棄分動畫)",
            () => ws({ type: "transCode", member_info_uid: uid, data: "ok" }),
            "green",
            {
              ws: true,
            },
          )}
          {btn(
            "Socket 錯誤訊息",
            () =>
              ws({
                type: "error",
                member_info_uid: uid,
                data: "【測試】Socket 錯誤訊息",
              }),
            "red",
            {
              ws: true,
            },
          )}

          {/* ⑤ 後端訊息翻譯 */}
          <Divider borderColor="whiteAlpha.200" my={1} />
          {toggleBtn("後端訊息(backend_response)", msgOpen, () =>
            setMsgOpen((v) => !v),
          )}
          {msgOpen && (
            <Flex direction="column" gap="4px">
              <Flex gap="4px">
                {[
                  ["toast", "Toast(API 錯誤)"],
                  ["socket", "Socket(error 推播)"],
                ].map(([value, label]) => (
                  <Button
                    key={value}
                    size="xs"
                    flex={1}
                    bg={channel === value ? GOLD : "transparent"}
                    color={channel === value ? "#1a1a1a" : GOLD}
                    border={`1px solid ${GOLD}`}
                    isDisabled={value === "socket" && !canWs}
                    onClick={() => setChannel(value)}
                  >
                    {label}
                  </Button>
                ))}
              </Flex>
              {channel === "socket" && uid == null && (
                <Text fontSize="9px" color="orange.300">
                  ⚠ 未登入:socket 推播對不上會員,點了不會有反應,請先登入或改用
                  Toast。
                </Text>
              )}
              <Flex gap="4px">
                <Input
                  size="xs"
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  placeholder="自訂(測未命中)"
                  color="white"
                />
                <Button
                  size="xs"
                  onClick={() =>
                    custom.trim() && fireBackendMessage(custom.trim())
                  }
                >
                  送出
                </Button>
              </Flex>
              <Input
                size="xs"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜尋(原文或翻譯)"
                color="white"
              />
              <Box maxH="32vh" overflowY="auto">
                {filteredKeys.length === 0 && (
                  <Text fontSize="10px" color="whiteAlpha.500">
                    沒有符合的訊息
                  </Text>
                )}
                {filteredKeys.map((key) => (
                  <Box
                    key={key}
                    as="button"
                    type="button"
                    title={key}
                    onClick={() => fireBackendMessage(key)}
                    display="block"
                    w="100%"
                    textAlign="left"
                    p="4px 6px"
                    mb="3px"
                    fontSize="10px"
                    bg="whiteAlpha.50"
                    border="1px solid"
                    borderColor="whiteAlpha.100"
                    borderRadius="4px"
                  >
                    <Text color={GOLD}>{String(backendMessages[key])}</Text>
                    <Text color="whiteAlpha.500" wordBreak="break-all">
                      {key}
                    </Text>
                  </Box>
                ))}
              </Box>
            </Flex>
          )}

          <Divider borderColor="whiteAlpha.200" my={1} />
          <Text fontSize="9px" color="whiteAlpha.500">
            僅測試環境顯示;正式站自動隱藏
          </Text>
        </Flex>
      )}
    </Box>
  );
};

export default DevTestPanel;
